import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useClientAuth } from '../context/ClientAuthContext';
import {
  User, Target, Activity, Dumbbell, Clock, Zap, Mail,
  ChevronRight, ChevronLeft, TrendingDown, TrendingUp, Minus,
  Sparkles, Salad, AlertCircle, Shapes, Users, ShieldCheck, Utensils, Timer
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import ResultsView from './calculator/ResultsView';
import CalculatorSEOContent from '../components/CalculatorSEOContent';
import { BackNav } from '../components/shared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CalorieCalculatorPage = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: clientUser } = useClientAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [isCalculating, setIsCalculating] = useState(false);
  const [results, setResults] = useState(null);
  const [calculationId, setCalculationId] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [proContent, setProContent] = useState(null);
  const [mealPlanContent, setMealPlanContent] = useState(null);
  const [mealPlanStructured, setMealPlanStructured] = useState(null);
  const [isLoadingPro, setIsLoadingPro] = useState(false);
  const [error, setError] = useState(null);
  const [discountMode, setDiscountMode] = useState(false);
  const [stats, setStats] = useState(null);
  const [genStep, setGenStep] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/calorie-calculator/stats`)
      .then((r) => r.json())
      .then((d) => { if (d && typeof d.weekly === 'number') setStats(d); })
      .catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    gender: 'male', age: 30, weight: 75, height: 175, target_weight: 70, body_fat: '',
    goal: 'weight_loss', deficit_surplus: 'moderate',
    activity_level: 'moderate',
    training_frequency: 3, training_type: 'musculation', training_intensity: 'moderate', training_duration: 60,
    training_time: '18:00', training_level: 'intermediate', recovery_quality: 'good',
    email: '', diet_preferences: '', allergies: '', cooking_time: 'normal',
    meals_per_day: 3, eating_pattern: 'classic', fasting_window: '12:00-20:00',
    regular_cycle: '', adapt_cycle: false, menopause: 'no',
    // Questionnaire morphotype (ecto / meso / endo)
    morpho_bone: 'meso', morpho_gain: 'meso', morpho_shape: 'meso', morpho_metab: 'meso'
  });

  // Pré-remplir l'email du client connecté (pour rattacher ses nouveaux calculs)
  useEffect(() => {
    if (clientUser?.email) {
      setFormData((prev) => (prev.email ? prev : { ...prev, email: clientUser.email }));
    }
  }, [clientUser]);

  // Pré-remplir tout le questionnaire avec le profil déjà connu du client (poids, taille,
  // âge, objectif, entraînement...) — il ne doit jamais avoir à ressaisir ce que l'app sait déjà.
  const PROFILE_PREFILL_FIELDS = [
    'gender', 'age', 'weight', 'height', 'target_weight', 'body_fat', 'goal', 'deficit_surplus',
    'activity_level', 'training_frequency', 'training_type', 'training_intensity', 'training_duration',
    'training_time', 'training_level', 'recovery_quality', 'diet_preferences', 'allergies',
    'cooking_time', 'meals_per_day', 'eating_pattern', 'fasting_window', 'regular_cycle',
    'adapt_cycle', 'menopause',
  ];
  useEffect(() => {
    if (!clientUser?.email) return;
    const token = localStorage.getItem('bodyup_client_token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/tracking/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((profile) => {
        if (!profile) return;
        setFormData((prev) => {
          const next = { ...prev };
          PROFILE_PREFILL_FIELDS.forEach((f) => {
            if (profile[f] !== undefined && profile[f] !== null && profile[f] !== '') next[f] = profile[f];
          });
          return next;
        });
      })
      .catch(() => {});
  }, [clientUser]);

  const totalSteps = 6;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const packageType = searchParams.get('package');
    const relanceId = searchParams.get('relance');
    const promo = searchParams.get('promo');
    const cid = searchParams.get('cid');
    if (sessionId && packageType) handleStripeReturn(sessionId, packageType);
    else if (relanceId) handleRelance(relanceId);
    else if (cid) handleViewSaved(cid);
    else if (promo === 'plan20') setDiscountMode(true);
  }, [searchParams]);

  const handleRelance = async (calcId) => {
    try {
      setIsLoadingPro(true);
      setShowResults(true);
      const res = await fetch(`${BACKEND_URL}/api/calorie-calculator/results/${calcId}?package=free`);
      const data = await res.json();
      if (data.results) {
        setResults(data.results);
        setCalculationId(calcId);
        setDiscountMode(true);
      } else {
        setError(language === 'en' ? 'Calculation not found.' : 'Calcul introuvable.');
      }
    } catch (e) {
      setError(language === 'en' ? 'An error occurred.' : 'Une erreur est survenue.');
    } finally {
      setIsLoadingPro(false);
    }
  };

  // Remonter en haut du questionnaire à chaque changement d'étape
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const fetchResults = async (calcId, packageType) => {
    const res = await fetch(`${BACKEND_URL}/api/calorie-calculator/results/${calcId}?package=${packageType}&origin=${encodeURIComponent(window.location.origin)}`);
    return res.json();
  };

  // Le plan alimentaire est généré en arrière-plan : on interroge le statut
  // jusqu'à ce qu'il soit prêt (max ~7.5 min — Claude Sonnet 4.6 prend plus de temps
  // que l'ancien modèle mais est bien plus fidèle au profil). Résilient aux blips transitoires.
  // On répercute l'étape réelle renvoyée par le backend ('plan' -> 'images' -> 'done')
  // sur la jauge du loader pour éviter qu'elle ne reste bloquée avant de sauter au résultat.
  const pollForContent = async (calcId, packageType) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const data = await fetchResults(calcId, packageType);
        if (data.status === 'error') return { error: true };
        if (data.progress_step) setGenStep(data.progress_step);
        if (data.meal_plan || (packageType === 'pro' && data.pro_analysis) || data.status === 'ready') {
          setGenStep('done');
          await new Promise((r) => setTimeout(r, 3000)); // laisse la jauge terminer sa course jusqu'à 100%
          return data;
        }
      } catch (e) {
        // réponse transitoire non-JSON pendant la génération : on continue
      }
    }
    return { error: true };
  };

  const handleStripeReturn = async (sessionId, packageType) => {
    try {
      setIsLoadingPro(true);
      setShowResults(true);
      // Poll payment status (Stripe may take a moment)
      let statusData = null;
      for (let i = 0; i < 6; i++) {
        const statusRes = await fetch(`${BACKEND_URL}/api/calorie-calculator/checkout/status/${sessionId}`);
        statusData = await statusRes.json();
        if (statusData.payment_status === 'paid' || statusData.status === 'expired') break;
        await new Promise((r) => setTimeout(r, 2000));
      }

      if (statusData && statusData.payment_status === 'paid') {
        const calcId = statusData.metadata.calculation_id;
        setCalculationId(calcId);
        let resultsData = await fetchResults(calcId, packageType);
        if (resultsData.results) setResults(resultsData.results);
        if (resultsData.status === 'generating') {
          setGenStep(resultsData.progress_step || 'plan');
          resultsData = await pollForContent(calcId, packageType);
        }
        if (resultsData.error) {
          setError(language === 'en' ? 'Your plan is taking longer than expected. It will arrive by email — please refresh in a minute.' : "Votre plan prend plus de temps que prévu. Il vous sera envoyé par email — actualisez dans une minute.");
          return;
        }
        if (resultsData.results) setResults(resultsData.results);
        if (packageType === 'pro') {
          setProContent(resultsData.pro_analysis);
        } else if (packageType === 'meal_plan') {
          setProContent(resultsData.pro_analysis);
          setMealPlanContent(resultsData.meal_plan);
          setMealPlanStructured(resultsData.meal_plan_structured || null);
        }
      } else {
        setError(language === 'en' ? 'Payment not confirmed. Please try again.' : "Paiement non confirmé. Veuillez réessayer.");
      }
    } catch (e) {
      setError(language === 'en' ? 'An error occurred while loading your content.' : "Une erreur est survenue lors du chargement.");
    } finally {
      setIsLoadingPro(false);
    }
  };

  const handleViewSaved = async (calcId) => {
    try {
      setIsLoadingPro(true);
      setShowResults(true);
      const token = localStorage.getItem('bodyup_client_token');
      const res = await fetch(`${BACKEND_URL}/api/auth/my-calculations/${calcId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (res.ok && data.results) {
        setResults({ ...data.results, goals_overridden: data.goals_overridden });
        setCalculationId(calcId);
        if (data.pro_analysis) setProContent(data.pro_analysis);
        if (data.meal_plan) setMealPlanContent(data.meal_plan);
        setMealPlanStructured(data.meal_plan_structured || null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (res.status === 401) {
        navigate('/espace-client');
      } else {
        setError(language === 'en' ? 'Result not found.' : 'Résultat introuvable.');
      }
    } catch (e) {
      setError(language === 'en' ? 'An error occurred.' : 'Une erreur est survenue.');
    } finally {
      setIsLoadingPro(false);
    }
  };

  const handleInputChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const nextStep = () => currentStep < totalSteps && setCurrentStep(currentStep + 1);
  const prevStep = () => currentStep > 1 && setCurrentStep(currentStep - 1);

  const handleCalculate = async () => {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((formData.email || '').trim());
    if (!emailOk) {
      setError(language === 'en' ? 'Please enter a valid email to receive your results.' : 'Veuillez saisir un email valide pour recevoir vos résultats.');
      setCurrentStep(6);
      return;
    }
    setIsCalculating(true);
    setError(null);
    try {
      const payload = { ...formData, morphotype: computeMorphotype(formData), language };
      if (payload.body_fat === '' || payload.body_fat === null || payload.body_fat === undefined) {
        delete payload.body_fat;
      } else {
        payload.body_fat = Number(payload.body_fat);
      }
      const response = await fetch(`${BACKEND_URL}/api/calorie-calculator/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('calc failed');
      const data = await response.json();

      // Flow "Nouveau calcul" depuis le tableau de bord (Mes objectifs) : le backend
      // synchronise déjà automatiquement les objectifs de suivi à chaque calcul, donc on
      // renvoie directement le client sur "Mes objectifs" à jour, sans afficher les résultats ici.
      if (searchParams.get('returnTo') === 'tracking' && clientUser) {
        toast.success(language === 'en' ? 'New calculation applied to your dashboard' : 'Nouveau calcul appliqué à votre tableau de bord');
        navigate('/mon-espace?tab=tracking');
        return;
      }

      setResults(data.results);
      setCalculationId(data.calculation_id);
      setShowResults(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(language === 'en' ? 'Calculation failed. Please check your inputs.' : 'Échec du calcul. Vérifiez vos informations.');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleUpgrade = async (packageType, promoCode) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/calorie-calculator/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ calculation_id: calculationId, package: packageType, origin_url: window.location.origin, discount: discountMode, promo_code: promoCode || undefined })
      });
      const data = await response.json();
      if (data.free_unlock) {
        if (data.results) setResults(data.results);
        if (data.pro_analysis) setProContent(data.pro_analysis);
        if (data.meal_plan) setMealPlanContent(data.meal_plan);
        if (data.meal_plan_structured) setMealPlanStructured(data.meal_plan_structured);
        toast.success(language === 'en' ? 'Unlocked with your promo code!' : 'Débloqué avec votre code promo !');
        return;
      }
      if (data.url) window.location.href = data.url;
      else throw new Error('no url');
    } catch (e) {
      setError(language === 'en' ? 'Payment could not be started. Please try again.' : 'Le paiement n\'a pas pu démarrer. Réessayez.');
    }
  };

  const handleRedeemCode = async (packageType, code) => {
    // Returns { ok, message } ; on success updates content state
    setIsLoadingPro(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/calorie-calculator/redeem-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ calculation_id: calculationId, package: packageType, code, origin_url: window.location.origin })
      });
      const data = await response.json();
      if (!response.ok) {
        return { ok: false, message: data.detail || (language === 'en' ? 'Invalid code' : 'Code invalide') };
      }
      if (data.needs_payment) {
        setIsLoadingPro(false);
        await handleUpgrade(packageType, code);
        return { ok: true, redirecting: true, message: language === 'en' ? `Code applied — final price: ${data.discounted_amount} CHF` : `Code appliqué — prix final : ${data.discounted_amount} CHF` };
      }
      if (data.results) setResults(data.results);
      let final = data;
      if (data.status === 'generating') {
        setGenStep(data.progress_step || 'plan');
        final = await pollForContent(calculationId, packageType);
      }
      if (final.error) {
        return { ok: false, message: language === 'en' ? 'Your plan is taking longer than expected. It will arrive by email — please refresh in a minute.' : "Votre plan prend plus de temps que prévu. Il vous sera envoyé par email — actualisez dans une minute." };
      }
      if (final.results) setResults(final.results);
      setProContent(final.pro_analysis);
      if (packageType === 'meal_plan') { setMealPlanContent(final.meal_plan); setMealPlanStructured(final.meal_plan_structured || null); }
      return { ok: true };
    } catch (e) {
      return { ok: false, message: language === 'en' ? 'An error occurred.' : 'Une erreur est survenue.' };
    } finally {
      setIsLoadingPro(false);
    }
  };

  const handleReset = () => {
    setShowResults(false);
    setResults(null);
    setProContent(null);
    setMealPlanContent(null);
    setMealPlanStructured(null);
    setCalculationId(null);
    setCurrentStep(1);
    setError(null);
    setGenStep(null);
    window.history.replaceState({}, '', '/calculateur-calories');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (showResults && (results || isLoadingPro)) {
    return (
      <ResultsView
        results={results || {}}
        formData={formData}
        calculationId={calculationId}
        proContent={proContent}
        mealPlanContent={mealPlanContent}
        mealPlanStructured={mealPlanStructured}
        onUpgrade={handleUpgrade}
        onRedeemCode={handleRedeemCode}
        isLoadingPro={isLoadingPro && !results}
        genStep={genStep}
        discountMode={discountMode}
        onReset={handleReset}
        language={language}
      />
    );
  }

  const stepIcons = [User, Target, Shapes, Activity, Dumbbell, Mail];
  const StepIcon = stepIcons[currentStep - 1];

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1ProfileForm formData={formData} onChange={handleInputChange} language={language} />;
      case 2: return <Step3GoalForm formData={formData} onChange={handleInputChange} language={language} />;
      case 3: return <Step2MorphotypeForm formData={formData} onChange={handleInputChange} language={language} />;
      case 4: return <Step4ActivityForm formData={formData} onChange={handleInputChange} language={language} />;
      case 5: return <Step5TrainingForm formData={formData} onChange={handleInputChange} language={language} />;
      case 6: return <Step6EmailForm formData={formData} onChange={handleInputChange} language={language} />;
      default: return null;
    }
  };

  return (
    <>
      <SEOHead
        title="Calculateur Calories & Macros Gratuit | Body-Up Nyon"
        description="Calculez vos calories, protéines, glucides et lipides selon votre objectif : perte de gras, prise de muscle, maintien ou recomposition corporelle. Gratuit, scientifique (Mifflin-St Jeor), version Pro + plan nutrition personnalisé."
        path="/calculateur-calories"
      />
      {/* Canonical unique du calculateur */}
      <link rel="canonical" href="https://body-up.ch/calculateur-calories" />
      <main className="min-h-screen bg-black pt-32 pb-12 relative overflow-hidden">
        {/* animated background blobs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/3 -right-32 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative">
          {/* Back Navigation */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
            <BackNav />
          </div>

          {/* Hero */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/20 to-yellow-500/20 rounded-full border border-primary/30 mb-6">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-bold text-white tracking-wider">{language === 'en' ? 'ULTRA-PRO CALCULATOR' : 'CALCULATEUR ULTRA-PRO'}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4">
              {language === 'en' ? 'Calorie Calculator' : 'Calculateur de Calories'}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-yellow-400">
                {language === 'en' ? 'Scientifically Accurate' : 'Scientifiquement Précis'}
              </span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl mx-auto">
              {language === 'en'
                ? 'Get your exact caloric needs with pro scientific formulas (Mifflin-St Jeor). Personalized meal plan available.'
                : 'Obtenez vos besoins caloriques exacts avec des formules scientifiques pro (Mifflin-St Jeor). Plan alimentaire personnalisé disponible.'}
            </p>

            {/* Social proof + reassurance bar */}
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3" data-testid="calculator-trust-bar">
              {stats && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                  className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur"
                  data-testid="calculator-weekly-counter"
                >
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                  </span>
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-sm text-zinc-200">
                    <span className="font-bold text-white">{stats.weekly.toLocaleString('fr-CH')}</span>{' '}
                    {language === 'en' ? 'people calculated this week' : 'personnes ont calculé cette semaine'}
                  </span>
                </motion.div>
              )}
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur"
                data-testid="calculator-no-card-badge"
              >
                <ShieldCheck className="w-4 h-4 text-green-400" />
                <span className="text-sm text-zinc-200">
                  {language === 'en' ? 'No card required for the free result' : 'Aucune carte requise pour le résultat gratuit'}
                </span>
              </motion.div>
            </div>
          </div>

          {/* Progress */}
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
            <div className="flex items-center gap-2 mb-2">
              {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                <div key={step} className="flex-1 h-2 rounded-full overflow-hidden bg-zinc-800">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-primary to-yellow-500"
                    initial={false}
                    animate={{ width: step <= currentStep ? '100%' : '0%' }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-zinc-500">
              {language === 'en' ? `Step ${currentStep} of ${totalSteps}` : `Étape ${currentStep} sur ${totalSteps}`}
            </p>
          </div>

          {/* Form card */}
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl" data-testid="calculator-card">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>

              {error && (
                <div className="mt-6 flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-sm" data-testid="calculator-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
              )}

              <div className="flex items-center justify-between mt-8 pt-8 border-t border-white/10">
                {currentStep > 1 ? (
                  <button onClick={prevStep} className="flex items-center gap-2 px-5 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors" data-testid="prev-step-btn">
                    <ChevronLeft className="w-5 h-5" /> {language === 'en' ? 'Back' : 'Retour'}
                  </button>
                ) : <div />}

                {currentStep < totalSteps ? (
                  <button onClick={nextStep} className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-primary to-pink-500 text-white font-bold rounded-xl transition-transform hover:scale-105" data-testid="next-step-btn">
                    {language === 'en' ? 'Next' : 'Suivant'} <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={handleCalculate}
                    disabled={isCalculating || !isValidEmail(formData.email)}
                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-yellow-400 via-primary to-pink-500 text-black font-bold rounded-xl transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    data-testid="calculate-btn"
                  >
                    {isCalculating ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black" /> {language === 'en' ? 'Calculating...' : 'Calcul en cours...'}</>
                    ) : (
                      <><Zap className="w-5 h-5" /> {language === 'en' ? 'Calculate Now' : 'Calculer Maintenant'}</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Contenu éducatif SEO sous le calculateur */}
      <CalculatorSEOContent language={language} />
    </>
  );
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '');

// Détermine le morphotype à partir des réponses du questionnaire (ecto/meso/endo)
const computeMorphotype = (fd) => {
  const tally = { ecto: 0, meso: 0, endo: 0 };
  [fd.morpho_bone, fd.morpho_gain, fd.morpho_shape, fd.morpho_metab].forEach((a) => {
    if (tally[a] !== undefined) tally[a] += 1;
  });
  if (tally.ecto > tally.meso && tally.ecto > tally.endo) return 'ectomorph';
  if (tally.endo > tally.meso && tally.endo > tally.ecto) return 'endomorph';
  return 'mesomorph';
};

/* ---------- Shared UI helpers ---------- */
const StepHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="text-center mb-8">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-yellow-500/20 border border-primary/30 mb-3">
      <Icon className="w-7 h-7 text-primary" />
    </div>
    <h2 className="text-2xl font-bold text-white mb-1">{title}</h2>
    <p className="text-zinc-400 text-sm">{subtitle}</p>
  </div>
);

const NumberStepper = ({ label, value, suffix, min, max, step = 1, onChange, testId }) => {
  const clamp = (v) => Math.min(max, Math.max(min, v));
  const dec = () => onChange(clamp((Number(value) || min) - step));
  const inc = () => onChange(clamp((Number(value) || min) + step));
  const handleInput = (e) => {
    const raw = e.target.value;
    if (raw === '') { onChange(''); return; }
    const v = parseInt(raw, 10);
    if (!isNaN(v)) onChange(v);
  };
  const handleBlur = () => {
    const v = parseInt(value, 10);
    onChange(isNaN(v) ? min : clamp(v));
  };
  return (
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <button
          type="button" onClick={dec} aria-label="moins"
          className="w-12 h-12 shrink-0 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-2xl font-bold flex items-center justify-center transition-colors active:scale-95"
          data-testid={`${testId}-dec`}
        >−</button>
        <div className="flex-1 relative">
          <input
            type="number" inputMode="numeric" pattern="[0-9]*"
            value={value} min={min} max={max}
            onChange={handleInput} onBlur={handleBlur}
            className="w-full text-center text-2xl font-bold bg-zinc-800 border border-white/10 rounded-xl py-2.5 text-white focus:border-primary focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            data-testid={testId}
          />
          {suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-zinc-500 pointer-events-none">{suffix}</span>}
        </div>
        <button
          type="button" onClick={inc} aria-label="plus"
          className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-primary to-pink-500 text-white text-2xl font-bold flex items-center justify-center transition-transform active:scale-95 hover:scale-105"
          data-testid={`${testId}-inc`}
        >+</button>
      </div>
    </div>
  );
};

const OptionCard = ({ active, onClick, icon: Icon, title, desc, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left w-full p-4 rounded-xl border transition-all ${active ? 'bg-gradient-to-r from-primary/20 to-pink-500/10 border-primary text-white' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
    data-testid={testId}
  >
    <div className="flex items-center gap-3">
      {Icon && <Icon className={`w-5 h-5 shrink-0 ${active ? 'text-primary' : 'text-zinc-500'}`} />}
      <div>
        <p className="font-semibold">{title}</p>
        {desc && <p className="text-xs text-zinc-500 mt-0.5">{desc}</p>}
      </div>
    </div>
  </button>
);

/* ---------- Step 1: Profile ---------- */
const Step1ProfileForm = ({ formData, onChange, language }) => (
  <div className="space-y-6">
    <StepHeader icon={User} title={language === 'en' ? 'Your Profile' : 'Votre Profil'} subtitle={language === 'en' ? "Let's start with the basics" : 'Commençons par les informations de base'} />
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-3">{language === 'en' ? 'Sex' : 'Sexe'}</label>
      <div className="grid grid-cols-2 gap-4">
        {['male', 'female'].map((g) => (
          <button key={g} onClick={() => onChange('gender', g)}
            className={`py-4 px-6 rounded-xl font-semibold transition-all ${formData.gender === g ? 'bg-gradient-to-r from-primary to-pink-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
            data-testid={`gender-${g}`}>
            {g === 'male' ? (language === 'en' ? 'Male' : 'Homme') : (language === 'en' ? 'Female' : 'Femme')}
          </button>
        ))}
      </div>
    </div>
    <NumberStepper label={language === 'en' ? 'Age' : 'Âge'} value={formData.age} suffix={language === 'en' ? 'years' : 'ans'} min={15} max={80} onChange={(v) => onChange('age', v)} testId="age-input" />
    <NumberStepper label={language === 'en' ? 'Current weight' : 'Poids actuel'} value={formData.weight} suffix="kg" min={40} max={200} onChange={(v) => onChange('weight', v)} testId="weight-input" />
    <NumberStepper label={language === 'en' ? 'Height' : 'Taille'} value={formData.height} suffix="cm" min={140} max={220} onChange={(v) => onChange('height', v)} testId="height-input" />
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-2">
        {language === 'en' ? 'Body fat %' : 'Masse grasse %'}
        <span className="ml-1 text-xs text-zinc-500">{language === 'en' ? '(optional)' : '(optionnel)'}</span>
      </label>
      <div className="relative">
        <input
          type="number" inputMode="decimal" min={5} max={50}
          value={formData.body_fat}
          onChange={(e) => onChange('body_fat', e.target.value)}
          placeholder={language === 'en' ? 'e.g. 18' : 'ex. 18'}
          className="w-full text-center text-xl font-bold bg-zinc-800 border border-white/10 rounded-xl py-2.5 text-white focus:border-primary focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          data-testid="bodyfat-input"
        />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-zinc-500 pointer-events-none">%</span>
      </div>
      <p className="text-xs text-zinc-500 mt-1.5">
        {language === 'en'
          ? 'Know it? We\'ll use the more precise Katch-McArdle formula.'
          : 'Vous le connaissez ? Nous utilisons alors la formule Katch-McArdle, plus précise.'}
      </p>
    </div>
  </div>
);

/* ---------- Step 2: Morphotype questionnaire ---------- */
const MorphoQuestion = ({ label, value, onChange, options, name }) => (
  <div>
    <label className="block text-sm font-medium text-zinc-300 mb-3">{label}</label>
    <div className="space-y-2.5">
      {options.map((o) => (
        <OptionCard
          key={o.id}
          active={value === o.id}
          onClick={() => onChange(name, o.id)}
          title={o.title}
          desc={o.desc}
          testId={`${name}-${o.id}`}
        />
      ))}
    </div>
  </div>
);

const Step2MorphotypeForm = ({ formData, onChange, language }) => {
  const en = language === 'en';
  return (
    <div className="space-y-7">
      <StepHeader icon={Shapes} title={en ? 'Your Morphotype' : 'Votre Morphotype'} subtitle={en ? 'A few questions to find your body type' : 'Quelques questions pour découvrir votre type de corps'} />
      <MorphoQuestion
        label={en ? '1. Your bone structure / wrists' : '1. Votre ossature / vos poignets'}
        name="morpho_bone" value={formData.morpho_bone} onChange={onChange}
        options={[
          { id: 'ecto', title: en ? 'Fine / small' : 'Fine / petite', desc: en ? 'Slim wrists, long limbs' : 'Poignets fins, membres longs' },
          { id: 'meso', title: en ? 'Medium / athletic' : 'Moyenne / athlétique', desc: en ? 'Naturally broad shoulders' : 'Épaules naturellement larges' },
          { id: 'endo', title: en ? 'Large / sturdy' : 'Large / robuste', desc: en ? 'Wide frame, thick joints' : 'Carrure large, articulations épaisses' },
        ]}
      />
      <MorphoQuestion
        label={en ? '2. You gain weight...' : '2. Vous prenez du poids...'}
        name="morpho_gain" value={formData.morpho_gain} onChange={onChange}
        options={[
          { id: 'ecto', title: en ? 'Very hardly' : 'Très difficilement', desc: en ? 'I struggle to gain anything' : "J'ai du mal à prendre quoi que ce soit" },
          { id: 'meso', title: en ? 'Easily as muscle' : 'Facilement, en muscle', desc: en ? 'I build muscle when I train' : "Je prends du muscle quand je m'entraîne" },
          { id: 'endo', title: en ? 'Easily as fat' : 'Facilement, en gras', desc: en ? 'I gain fat quickly' : 'Je stocke vite de la graisse' },
        ]}
      />
      <MorphoQuestion
        label={en ? '3. Your natural silhouette' : '3. Votre silhouette naturelle'}
        name="morpho_shape" value={formData.morpho_shape} onChange={onChange}
        options={[
          { id: 'ecto', title: en ? 'Slim / lean' : 'Élancée / mince', desc: en ? 'Flat, narrow' : 'Plate, étroite' },
          { id: 'meso', title: en ? 'Athletic / V-shape' : 'Athlétique / en V', desc: en ? 'Broad shoulders, narrow waist' : 'Épaules larges, taille fine' },
          { id: 'endo', title: en ? 'Rounder' : 'Plus ronde', desc: en ? 'Stores around belly/hips' : 'Stocke au ventre / hanches' },
        ]}
      />
      <MorphoQuestion
        label={en ? '4. Your metabolism / appetite' : '4. Votre métabolisme / appétit'}
        name="morpho_metab" value={formData.morpho_metab} onChange={onChange}
        options={[
          { id: 'ecto', title: en ? 'I can eat a lot without gaining' : 'Je mange beaucoup sans grossir', desc: en ? 'Fast metabolism' : 'Métabolisme rapide' },
          { id: 'meso', title: en ? 'Balanced' : 'Équilibré', desc: en ? 'Responds well to diet/training' : 'Réagit bien au régime/sport' },
          { id: 'endo', title: en ? 'I gain even eating little' : 'Je grossis même en mangeant peu', desc: en ? 'Slow metabolism' : 'Métabolisme lent' },
        ]}
      />
    </div>
  );
};

/* ---------- Step 3: Goal ---------- */
const GOAL_DESCRIPTIONS = {
  weight_loss: {
    fr: "Diminuer la masse grasse en conservant un maximum de muscle. La balance baisse, le ventre s'affine et la silhouette devient plus définie.",
    en: "Reduce body fat while preserving muscle. The scale drops, your waist slims and your shape becomes more defined.",
  },
  muscle_gain: {
    fr: "Développer la masse musculaire, gagner en force et en volume. Le poids peut monter (le muscle est dense), mais le corps devient plus ferme et plus athlétique.",
    en: "Build muscle mass, gain strength and size. Your weight may go up (muscle is dense), but your body becomes firmer and more athletic.",
  },
  recomposition: {
    fr: "Perdre du gras et construire du muscle en même temps. Le poids bouge peu, mais la silhouette change fortement : moins de gras, plus de tonicité et de définition.",
    en: "Lose fat and build muscle at the same time. The scale barely moves but your shape changes a lot: less fat, more tone and definition.",
  },
  maintenance: {
    fr: "Conserver son poids, sa masse musculaire et sa forme actuelle. Idéal pour stabiliser les résultats acquis et garder une bonne énergie au quotidien.",
    en: "Keep your current weight, muscle mass and shape. Ideal to lock in results and stay energized day-to-day.",
  },
};

const GoalCard = ({ goal, isSelected, onChange, language }) => {
  const [showTip, setShowTip] = React.useState(false);
  const en = language === 'en';
  const desc = GOAL_DESCRIPTIONS[goal.id]?.[en ? 'en' : 'fr'] || '';
  const Icon = goal.icon;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onChange('goal', goal.id)}
        className={`w-full flex flex-col items-center gap-2 py-5 rounded-xl border transition-all ${isSelected ? 'bg-gradient-to-b from-primary/20 to-pink-500/10 border-primary text-white' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
        data-testid={`goal-${goal.id}`}
      >
        <Icon className={`w-7 h-7 ${isSelected ? 'text-primary' : 'text-zinc-500'}`} />
        <span className="text-sm font-semibold text-center px-1">{goal.title}</span>
      </button>

      {/* Info button (hover desktop / tap mobile) */}
      <button
        type="button"
        aria-label={en ? `What is ${goal.title}?` : `Qu'est-ce que ${goal.title} ?`}
        onClick={(e) => { e.stopPropagation(); setShowTip(v => !v); }}
        onMouseEnter={() => setShowTip(true)}
        onMouseLeave={() => setShowTip(false)}
        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/50 hover:bg-primary/30 border border-white/10 flex items-center justify-center text-[10px] font-bold text-white/70 hover:text-white transition-colors z-10"
        data-testid={`goal-tooltip-trigger-${goal.id}`}
      >
        ?
      </button>

      {/* Tooltip */}
      {showTip && (
        <div
          className="absolute z-30 top-full left-1/2 -translate-x-1/2 mt-2 w-64 max-w-[280px] px-4 py-3 rounded-xl bg-black border border-primary/40 shadow-2xl text-xs leading-relaxed text-zinc-200"
          data-testid={`goal-tooltip-${goal.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="font-bold text-primary mb-1.5">{goal.title}</div>
          <p>{desc}</p>
          {/* Arrow */}
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-black border-l border-t border-primary/40" />
        </div>
      )}
    </div>
  );
};

const Step3GoalForm = ({ formData, onChange, language }) => {
  const goals = [
    { id: 'weight_loss', icon: TrendingDown, title: language === 'en' ? 'Fat loss' : 'Perte de gras' },
    { id: 'recomposition', icon: Shapes, title: language === 'en' ? 'Body recomp' : 'Recomposition corporelle' },
    { id: 'maintenance', icon: Minus, title: language === 'en' ? 'Maintenance' : 'Maintien' },
    { id: 'muscle_gain', icon: TrendingUp, title: language === 'en' ? 'Muscle gain' : 'Prise de muscle' },
  ];
  const intensities = [
    { id: 'gentle', title: language === 'en' ? 'Gentle' : 'Doux' },
    { id: 'moderate', title: language === 'en' ? 'Moderate' : 'Modéré' },
    { id: 'aggressive', title: language === 'en' ? 'Aggressive' : 'Agressif' },
  ];
  return (
    <div className="space-y-6">
      <StepHeader icon={Target} title={language === 'en' ? 'Your Goal' : 'Votre Objectif'} subtitle={language === 'en' ? 'What do you want to achieve?' : 'Que souhaitez-vous accomplir ?'} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {goals.map((g) => (
          <GoalCard
            key={g.id}
            goal={g}
            isSelected={formData.goal === g.id}
            onChange={onChange}
            language={language}
          />
        ))}
      </div>

      {(formData.goal === 'weight_loss' || formData.goal === 'muscle_gain') && (
        <NumberStepper
          label={language === 'en' ? 'Target weight' : 'Poids cible'}
          value={formData.target_weight} suffix="kg" min={40} max={200}
          onChange={(v) => onChange('target_weight', v)} testId="target-weight-input"
        />
      )}

      {(formData.goal === 'weight_loss' || formData.goal === 'muscle_gain') && (
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-3">{language === 'en' ? 'Pace' : 'Intensité'}</label>
          <div className="grid grid-cols-3 gap-3">
            {intensities.map((it) => (
              <button key={it.id} onClick={() => onChange('deficit_surplus', it.id)}
                className={`py-3 rounded-xl text-sm font-semibold border transition-all ${formData.deficit_surplus === it.id ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
                data-testid={`deficit-${it.id}`}>
                {it.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Step 4: Activity ---------- */
const Step4ActivityForm = ({ formData, onChange, language }) => {
  const levels = [
    { id: 'sedentary', title: language === 'en' ? 'Sedentary' : 'Sédentaire', desc: language === 'en' ? 'Desk job, little movement' : 'Bureau, peu de mouvement' },
    { id: 'light', title: language === 'en' ? 'Light' : 'Légère', desc: language === 'en' ? 'Occasional walking' : 'Marche occasionnelle' },
    { id: 'moderate', title: language === 'en' ? 'Moderate' : 'Modérée', desc: language === 'en' ? 'Active daily' : 'Actif quotidiennement' },
    { id: 'active', title: language === 'en' ? 'Active' : 'Active', desc: language === 'en' ? 'Regular sport' : 'Sport régulier' },
    { id: 'very_active', title: language === 'en' ? 'Very active' : 'Très active', desc: language === 'en' ? 'Physical job / athlete' : 'Travail physique' },
  ];
  return (
    <div className="space-y-4">
      <StepHeader icon={Activity} title={language === 'en' ? 'Daily Activity' : 'Activité Quotidienne'} subtitle={language === 'en' ? 'Excluding workouts' : 'Hors séances de sport'} />
      <div className="space-y-3">
        {levels.map((l) => (
          <OptionCard key={l.id} active={formData.activity_level === l.id} onClick={() => onChange('activity_level', l.id)} title={l.title} desc={l.desc} testId={`activity-${l.id}`} />
        ))}
      </div>
    </div>
  );
};

/* ---------- Step 5: Training ---------- */
const Step5TrainingForm = ({ formData, onChange, language }) => {
  const types = [
    { id: 'musculation', label: language === 'en' ? 'Strength training' : 'Musculation' },
    { id: 'crossfit', label: 'CrossFit' },
    { id: 'natation', label: language === 'en' ? 'Swimming' : 'Natation' },
    { id: 'course', label: language === 'en' ? 'Running / Jogging' : 'Course à pied / Jogging' },
    { id: 'velo', label: language === 'en' ? 'Cycling' : 'Vélo / Cyclisme' },
    { id: 'marche', label: language === 'en' ? 'Brisk walking' : 'Marche rapide' },
    { id: 'boxe', label: language === 'en' ? 'Boxing / Combat' : 'Boxe / Sports de combat' },
    { id: 'hiit', label: 'HIIT' },
    { id: 'circuit', label: 'Circuit training' },
    { id: 'ems', label: 'EMS (Body-Up)' },
    { id: 'corde_sauter', label: language === 'en' ? 'Jump rope' : 'Corde à sauter' },
    { id: 'rameur', label: language === 'en' ? 'Rowing' : 'Rameur' },
    { id: 'sport_collectif', label: language === 'en' ? 'Team sport (football, basket...)' : 'Sport collectif (foot, basket...)' },
    { id: 'yoga', label: 'Yoga / Pilates' },
    { id: 'other', label: language === 'en' ? 'Other' : 'Autre' },
  ];
  const intensities = [
    { id: 'light', title: language === 'en' ? 'Light' : 'Légère', desc: language === 'en' ? 'recovery, technique' : 'récup, technique' },
    { id: 'moderate', title: language === 'en' ? 'Moderate' : 'Modérée', desc: language === 'en' ? 'sustained effort' : 'effort soutenu' },
    { id: 'intense', title: language === 'en' ? 'Intense' : 'Intense', desc: language === 'en' ? 'maximal, breathless' : 'maximal, essoufflé' },
  ];
  return (
    <div className="space-y-6">
      <StepHeader icon={Dumbbell} title={language === 'en' ? 'Training' : 'Entraînement'} subtitle={language === 'en' ? 'Tell us about your workouts' : 'Parlez-nous de vos séances'} />
      <NumberStepper label={language === 'en' ? 'Sessions / week' : 'Séances / semaine'} value={formData.training_frequency} suffix={language === 'en' ? 'days' : 'jours'} min={0} max={7} onChange={(v) => onChange('training_frequency', v)} testId="frequency-input" />

      {formData.training_frequency > 0 && (
        <>
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">{language === 'en' ? 'Main activity' : 'Activité principale'}</label>
            <select
              value={formData.training_type}
              onChange={(e) => onChange('training_type', e.target.value)}
              className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary focus:outline-none"
              data-testid="training-type-select"
            >
              {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-3">{language === 'en' ? 'Intensity' : 'Intensité'}</label>
            <div className="grid grid-cols-3 gap-3">
              {intensities.map((it) => (
                <button key={it.id} onClick={() => onChange('training_intensity', it.id)}
                  className={`flex flex-col items-center py-3 px-2 rounded-xl border transition-all ${formData.training_intensity === it.id ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
                  data-testid={`intensity-${it.id}`}>
                  <span className="text-sm font-semibold">{it.title}</span>
                  <span className="text-[10px] opacity-70 mt-0.5 text-center leading-tight">{it.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-zinc-300">
            <Clock className="w-4 h-4 text-primary shrink-0" />
            <div className="flex-1">
              <NumberStepper label={language === 'en' ? 'Session duration' : 'Durée séance'} value={formData.training_duration} suffix="min" min={15} max={180} step={5} onChange={(v) => onChange('training_duration', v)} testId="duration-input" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" /> {language === 'en' ? 'Usual training time' : "Heure habituelle d'entraînement"}
            </label>
            <input
              type="time"
              value={formData.training_time}
              onChange={(e) => onChange('training_time', e.target.value)}
              className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary focus:outline-none"
              data-testid="training-time-input"
            />
            <p className="text-xs text-zinc-500 mt-1.5">
              {language === 'en'
                ? 'Used to time your meals around your workout (pre/post nutrition).'
                : "Sert à caler vos repas autour de votre séance (nutrition avant/après)."}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-3">{language === 'en' ? 'Your level' : 'Votre niveau'}</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'beginner', title: language === 'en' ? 'Beginner' : 'Débutant' },
                { id: 'intermediate', title: language === 'en' ? 'Intermediate' : 'Intermédiaire' },
                { id: 'advanced', title: language === 'en' ? 'Advanced' : 'Avancé' },
              ].map((l) => (
                <button key={l.id} type="button" onClick={() => onChange('training_level', l.id)}
                  className={`py-3 rounded-xl text-sm font-semibold border transition-all ${formData.training_level === l.id ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
                  data-testid={`level-${l.id}`}>
                  {l.title}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-1.5">{language === 'en' ? 'Recovery (sleep & stress)' : 'Récupération (sommeil & stress)'}</label>
        <p className="text-xs text-zinc-500 mb-3">{language === 'en' ? 'Optional but recommended for a more accurate result' : 'Optionnel mais recommandé pour un résultat plus précis'}</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: 'good', title: language === 'en' ? 'Good' : 'Bonne' },
            { id: 'medium', title: language === 'en' ? 'Average' : 'Moyenne' },
            { id: 'poor', title: language === 'en' ? 'Poor' : 'Faible' },
          ].map((r) => (
            <button key={r.id} type="button" onClick={() => onChange('recovery_quality', r.id)}
              className={`py-3 rounded-xl text-sm font-semibold border transition-all ${formData.recovery_quality === r.id ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
              data-testid={`recovery-${r.id}`}>
              {r.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------- Step 6: Email & Preferences ---------- */
const Step6EmailForm = ({ formData, onChange, language }) => {
  const en = language === 'en';
  return (
  <div className="space-y-6">
    <StepHeader icon={Mail} title={en ? 'Almost done!' : 'Presque terminé !'} subtitle={en ? 'A few preferences to tailor your plan' : 'Quelques préférences pour personnaliser votre programme'} />
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-2">Email *</label>
      <input
        type="email" value={formData.email} required
        onChange={(e) => onChange('email', e.target.value)}
        placeholder="vous@exemple.com"
        className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:border-primary focus:outline-none"
        data-testid="email-input"
      />
    </div>

    {formData.gender === 'female' && (
      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 space-y-4" data-testid="women-block">
        <p className="text-sm font-semibold text-white flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" /> {en ? 'For women (optional, recommended)' : 'Pour les femmes (optionnel, recommandé)'}
        </p>
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2">{en ? 'Peri/menopause?' : 'Périménopause ou ménopause ?'}</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'no', title: en ? 'No' : 'Non' },
              { id: 'peri', title: en ? 'Perimenopause' : 'Périménopause' },
              { id: 'meno', title: en ? 'Menopause' : 'Ménopause' },
            ].map((m) => (
              <button key={m.id} type="button" onClick={() => onChange('menopause', m.id)}
                className={`py-2.5 rounded-xl text-xs font-semibold border transition-all ${formData.menopause === m.id ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
                data-testid={`menopause-${m.id}`}>{m.title}</button>
            ))}
          </div>
        </div>
        {formData.menopause !== 'meno' && (
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">{en ? 'Regular menstrual cycle?' : 'Cycle menstruel régulier ?'}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'yes', title: en ? 'Yes' : 'Oui' },
                { id: 'no', title: en ? 'No / not sure' : 'Non / je ne sais pas' },
              ].map((c) => (
                <button key={c.id} type="button" onClick={() => onChange('regular_cycle', c.id)}
                  className={`py-2.5 rounded-xl text-xs font-semibold border transition-all ${formData.regular_cycle === c.id ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
                  data-testid={`cycle-${c.id}`}>{c.title}</button>
              ))}
            </div>
            {formData.regular_cycle === 'yes' && (
              <label className="flex items-center gap-2 mt-3 text-sm text-zinc-300 cursor-pointer" data-testid="adapt-cycle-toggle">
                <input type="checkbox" checked={formData.adapt_cycle} onChange={(e) => onChange('adapt_cycle', e.target.checked)} className="w-4 h-4 accent-pink-500" />
                {en ? 'Adapt my recommendations to my cycle' : 'Adapter mes recommandations selon mon cycle'}
              </label>
            )}
          </div>
        )}
      </div>
    )}

    {/* Meal frequency */}
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
        <Utensils className="w-4 h-4 text-primary" /> {en ? 'Meals per day you prefer' : 'Nombre de repas par jour souhaité'}
      </label>
      <div className="grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n} type="button" onClick={() => onChange('meals_per_day', n)}
            className={`py-3 rounded-xl text-lg font-bold border transition-all ${formData.meals_per_day === n ? 'bg-gradient-to-r from-primary to-pink-500 text-white border-transparent' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
            data-testid={`meals-${n}`}
          >{n}</button>
        ))}
      </div>
      <p className="text-xs text-zinc-500 mt-1.5">
        {en ? 'Your plan will respect exactly this number of meals — no more, no less.' : 'Votre programme respectera exactement ce nombre de repas — ni plus, ni moins.'}
      </p>
    </div>

    {/* Eating pattern */}
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
        <Timer className="w-4 h-4 text-primary" /> {en ? 'Eating pattern' : 'Rythme alimentaire'}
      </label>
      <div className="grid grid-cols-2 gap-3">
        {[
          { id: 'classic', title: en ? 'Classic' : 'Classique', desc: en ? 'Spread over the day' : 'Réparti sur la journée' },
          { id: 'intermittent', title: en ? 'Intermittent fasting' : 'Jeûne intermittent', desc: en ? 'Eating window only' : 'Sur une fenêtre horaire' },
        ].map((p) => (
          <button
            key={p.id} type="button" onClick={() => onChange('eating_pattern', p.id)}
            className={`text-left p-4 rounded-xl border transition-all ${formData.eating_pattern === p.id ? 'bg-gradient-to-r from-primary/20 to-pink-500/10 border-primary text-white' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
            data-testid={`pattern-${p.id}`}
          >
            <p className="font-semibold text-sm">{p.title}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{p.desc}</p>
          </button>
        ))}
      </div>
      {formData.eating_pattern === 'intermittent' && (
        <div className="mt-3">
          <label className="block text-xs font-medium text-zinc-400 mb-2">{en ? 'Eating window' : 'Fenêtre alimentaire'}</label>
          <select
            value={formData.fasting_window}
            onChange={(e) => onChange('fasting_window', e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-primary focus:outline-none"
            data-testid="fasting-window-select"
          >
            <option value="12:00-20:00">16/8 — 12h00 → 20h00</option>
            <option value="13:00-19:00">18/6 — 13h00 → 19h00</option>
            <option value="16:00-20:00">20/4 — 16h00 → 20h00</option>
            <option value="10:00-18:00">16/8 — 10h00 → 18h00</option>
            <option value="OMAD">OMAD — {en ? '1 single meal' : '1 seul repas'}</option>
          </select>
        </div>
      )}
    </div>

    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
        <Salad className="w-4 h-4 text-primary" /> {en ? 'Dietary preferences (optional)' : 'Préférences alimentaires (optionnel)'}
      </label>
      <textarea
        value={formData.diet_preferences} rows={2}
        onChange={(e) => onChange('diet_preferences', e.target.value)}
        placeholder={en ? 'Vegetarian, vegan, gluten-free...' : 'Végétarien, végan, sans gluten...'}
        className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:border-primary focus:outline-none resize-none"
        data-testid="diet-preferences-input"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-2">{en ? 'Allergies (optional)' : 'Allergies (optionnel)'}</label>
      <textarea
        value={formData.allergies} rows={2}
        onChange={(e) => onChange('allergies', e.target.value)}
        placeholder={en ? 'Nuts, lactose, shellfish...' : 'Fruits à coque, lactose, fruits de mer...'}
        className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:border-primary focus:outline-none resize-none"
        data-testid="allergies-input"
      />
    </div>

    {/* Cooking time (used for the personalized meal plan) */}
    <div>
      <label className="block text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
        <Utensils className="w-4 h-4 text-primary" /> {en ? 'Time for cooking' : 'Temps en cuisine'}
      </label>
      <div className="grid grid-cols-3 gap-3">
        {[
          { id: 'express', title: 'Express', desc: '< 10 min' },
          { id: 'normal', title: 'Normal', desc: '15-30 min' },
          { id: 'love', title: en ? 'I love it' : "J'aime cuisiner", desc: en ? 'Elaborate' : 'Élaboré' },
        ].map((c) => (
          <button key={c.id} type="button" onClick={() => onChange('cooking_time', c.id)}
            className={`py-3 px-2 rounded-xl border transition-all text-center ${formData.cooking_time === c.id ? 'bg-gradient-to-r from-primary/20 to-pink-500/10 border-primary text-white' : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:border-white/20'}`}
            data-testid={`cooking-${c.id}`}>
            <span className="block text-sm font-semibold">{c.title}</span>
            <span className="block text-[10px] opacity-70 mt-0.5">{c.desc}</span>
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};

export default CalorieCalculatorPage;
